import React, { useState, useMemo } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  useColorModeValue,
  Flex,
  Badge,
  IconButton,
} from '@chakra-ui/react';
import { RepeatIcon } from '@chakra-ui/icons';
import DeclarationList from './DeclarationList';

const DeclarationTabs = ({ declarations, userRole, onRefresh, refreshing = false }) => {
  const [activeTab, setActiveTab] = useState(userRole === 'Commercial' ? 'validation' : 'cheque');
  
  const tabConfig = [
    { key: 'cheque', label: 'Chèques', color: 'blue' },
    { key: 'effet', label: 'Effets', color: 'green' },
    { key: 'espece', label: 'Espèces', color: 'orange' },
    { key: 'versement deplacer', label: 'Versements Déplacés', color: 'pink' },
    { key: 'note de frais', label: 'Notes de Frais', color: 'yellow' },
  ];

  // Add virement tab for Technico and Commercial
  if (userRole === 'Technico' || userRole === 'Commercial') {
    tabConfig.push({ key: 'virement', label: 'Virements', color: 'purple' });
  }

  // Add compte client tab for Commercial
  if (userRole === 'Commercial') {
    tabConfig.push({ key: 'compte client', label: 'Compte Client', color: 'teal' });
  }

  // Add validation tab for Commercial
  if (userRole === 'Commercial') {
    tabConfig.unshift({ key: 'validation', label: 'À valider', color: 'red' });
  }

  const filteredDeclarations = useMemo(() => {
    let filtered = [];
    
    if (activeTab === 'validation') {
      // Show only unvalidated declarations for Commercial
      filtered = declarations.filter(declaration => declaration.is_valid === 0);
    } else if (userRole === 'Technico') {
      // For Technico: show only unvalidated declarations of the selected type
      filtered = declarations.filter(declaration => 
        declaration.type === activeTab && declaration.is_valid === 0
      );
    } else {
      // For Commercial: show all declarations of the selected type
      filtered = declarations.filter(declaration => declaration.type === activeTab);
    }

    // Sort by date_valeur from newest to oldest for "compte client" type
    if (activeTab === 'compte client') {
      filtered.sort((a, b) => {
        const dateA = new Date(a.date_valeur);
        const dateB = new Date(b.date_valeur);
        return dateB - dateA; // Newest first
      });
    }

    return filtered;
  }, [declarations, activeTab, userRole]);

  const getTabCount = (type) => {
    if (type === 'validation') {
      // Count unvalidated declarations for Commercial
      return declarations.filter(declaration => declaration.is_valid === 0).length;
    } else if (userRole === 'Technico') {
      // For Technico: count only unvalidated declarations of this type
      return declarations.filter(declaration => 
        declaration.type === type && declaration.is_valid === 0
      ).length;
    } else {
      // For Commercial: count all declarations of this type
      return declarations.filter(declaration => declaration.type === type).length;
    }
  };

  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={4}>
        <Text fontSize="lg" fontWeight="semibold">
          {userRole === 'Technico' ? 'Déclarations non validées' : 'Mes déclarations'}
        </Text>
        <IconButton
          icon={<RepeatIcon />}
          onClick={onRefresh}
          variant="ghost"
          size="sm"
          aria-label="Actualiser les données"
          title="Actualiser les données"
          isLoading={refreshing}
        />
      </Flex>
      
      {/* Tab Buttons */}
      <Flex
        bg={bg}
        borderRadius="lg"
        p={1}
        border="1px solid"
        borderColor={borderColor}
        mb={6}
        overflowX="auto"
        sx={{
          '&::-webkit-scrollbar': {
            height: '4px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: useColorModeValue('gray.300', 'gray.600'),
            borderRadius: '2px',
          },
        }}
      >
        {tabConfig.map((tab) => (
          <Button
            key={tab.key}
            variant={activeTab === tab.key ? 'solid' : 'ghost'}
            colorScheme={tab.color}
            size="md"
            flexShrink={0}
            onClick={() => setActiveTab(tab.key)}
            borderRadius="md"
            mr={2}
            _last={{ mr: 0 }}
            position="relative"
          >
            {tab.label}
            <Badge
              ml={2}
              colorScheme={tab.color}
              variant="subtle"
              fontSize="xs"
              borderRadius="full"
              px={2}
            >
              {getTabCount(tab.key)}
            </Badge>
          </Button>
        ))}
      </Flex>

      {/* Declaration List */}
      <DeclarationList 
        declarations={filteredDeclarations}
        type={activeTab}
        onRefresh={onRefresh}
        userRole={userRole}
      />
    </Box>
  );
};

export default DeclarationTabs; 